import { useState, useEffect, useRef } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { useAuthStore, ROLES } from '../../shared/store/authStore'
import { Home, Scissors, Package, Cog, ArrowDown, Flame, Circle, Truck, LogOut } from 'lucide-react'

const MM_TABS = [
  { to: '/app/dashboard', icon: Home,    label: 'Home' },
  { to: '/app/cutting',   icon: Scissors, label: 'Cutting' },
  { to: '/app/bobbin',    icon: Package,  label: 'Bobbin' },
]

const LS_TABS = [
  { to: '/app/dashboard', icon: Home,     label: 'Home' },
  { to: '/app/spindle',   icon: Cog,      label: 'Spindle' },
  { to: '/app/output',    icon: ArrowDown, label: 'Output' },
  { to: '/app/boiling',   icon: Flame,    label: 'Boiling' },
  { to: '/app/cone',      icon: Circle,   label: 'Cone' },
  { to: '/app/dispatch', icon: Truck,    label: 'Dispatch' },
]

export default function TabletShell() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const tabs = user?.role === ROLES.METALLIC_MASTER ? MM_TABS : LS_TABS
  const isTeal = user?.role === ROLES.LOADING_SUPERVISOR

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // --- Pull to Refresh Logic ---
  const [pullProgress, setPullProgress] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const containerRef = useRef(null)
  const startY = useRef(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const handleTouchStart = (e) => {
      if (el.scrollTop === 0) {
        startY.current = e.touches[0].pageY
      } else {
        startY.current = -1
      }
    }

    const handleTouchMove = (e) => {
      if (startY.current === -1) return
      const y = e.touches[0].pageY
      const dist = y - startY.current
      if (dist > 0) {
        if (dist < 150) {
          setPullProgress(dist)
          if (e.cancelable) e.preventDefault()
        }
      }
    }

    const handleTouchEnd = () => {
      if (pullProgress > 80) {
        setIsRefreshing(true)
        // Global refresh
        window.location.reload()
      }
      setPullProgress(0)
      startY.current = -1
    }

    el.addEventListener('touchstart', handleTouchStart, { passive: false })
    el.addEventListener('touchmove', handleTouchMove, { passive: false })
    el.addEventListener('touchend', handleTouchEnd)

    return () => {
      el.removeEventListener('touchstart', handleTouchStart)
      el.removeEventListener('touchmove', handleTouchMove)
      el.removeEventListener('touchend', handleTouchEnd)
    }
  }, [pullProgress])

  return (
    <div className="min-h-screen bg-surface-page flex flex-col">
      {/* Top identity bar */}
      <div className={clsx(
        'flex items-center justify-between px-4 py-2 flex-shrink-0',
        isTeal ? 'bg-teal-600' : 'bg-brand-600'
      )}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-white/20 rounded font-bold text-white text-[10px] flex items-center justify-center font-mono">JT</div>
          <span className="text-white text-xs font-medium">{user?.name || 'Supervisor'}</span>
          <span className="text-white/50 text-xs">·</span>
          <span className="text-white/70 text-xs">{user?.unitName || 'Unit A'}</span>
        </div>
        <button onClick={handleLogout} className="text-white/60 hover:text-white transition-colors p-1">
          <LogOut size={14} />
        </button>
      </div>

      {/* Page content */}
      <div 
        ref={containerRef}
        className="flex-1 pb-16 overflow-y-auto relative"
      >
        {pullProgress > 0 && (
          <div 
            className="absolute top-0 left-0 right-0 flex items-center justify-center py-4 z-50 pointer-events-none"
            style={{ transform: `translateY(${Math.min(pullProgress - 40, 40)}px)`, opacity: pullProgress / 80 }}
          >
            <div className={clsx(
              "w-8 h-8 rounded-full border-2 border-t-transparent animate-spin",
              isTeal ? "border-teal-600" : "border-brand-600"
            )} />
          </div>
        )}
        <Outlet />
      </div>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 flex z-40 safe-area-bottom">
        {tabs.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/app/dashboard'}
            className={({ isActive }) =>
              clsx(
                'flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors',
                isActive
                  ? isTeal ? 'text-teal-600' : 'text-brand-600'
                  : 'text-gray-400'
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={18} className={isActive ? isTeal ? 'text-teal-600' : 'text-brand-600' : 'text-gray-400'} />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
